import React from 'react';
import { Helmet } from 'react-helmet-async';

const Section = ({ title, children }) => (
  <section className="mb-14">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-1 h-8 bg-brand-500 rounded-full shrink-0" aria-hidden="true" />
      <h2 className="text-3xl font-extrabold text-navy-900">{title}</h2>
    </div>
    {children}
  </section>
);

const AboutUsPage = () => (
  <>
    <Helmet>
      <title>About Mato Industries – 55+ Years of Precision Manufacturing</title>
      <meta name="description" content="Founded in 1968 by Mr. Ramesh Jain, Mato Industries is a Tier-1 OEM supplier of bronze bushings and copper alloy components to global manufacturers. Delhi, India." />
      <link rel="canonical" href="https://www.matoindustries.com/about" />
      <meta property="og:url" content="https://www.matoindustries.com/about" />
      <meta property="og:title" content="About Mato Industries – 55+ Years of Precision Manufacturing" />
      <meta property="og:description" content="Founded in 1968 by Mr. Ramesh Jain, Mato Industries is a Tier-1 OEM supplier of bronze bushings and copper alloy components to global manufacturers." />
    </Helmet>
    <div className="min-h-screen bg-slate-50">

    {/* Page header */}
    <div className="bg-navy-900 text-white py-14 px-4">
      <div className="container mx-auto max-w-7xl">
        <span className="text-brand-400 text-sm font-semibold uppercase tracking-widest">Company</span>
        <h1 className="text-4xl md:text-5xl font-extrabold mt-2 mb-3">About Mato Industries</h1>
        <p className="text-slate-400 text-lg max-w-2xl">
          ISO 9001 certified manufacturer of bronze and copper alloy components, trusted by global OEMs since 1968.
        </p>
      </div>
    </div>

    <main className="container mx-auto max-w-7xl px-4 sm:px-6 py-14">

      {/* About */}
      <Section title="Who We Are">
        <div className="prose prose-slate max-w-none text-slate-700 text-base leading-relaxed space-y-4">
          <p>
            <strong>Founded in 1968, Mato Industries</strong> is an ISO 9001-certified leader in{' '}
            <strong>Bronze Bushings, Washers, and precision-engineered copper alloy components</strong>. With over five
            decades of specialized experience, we have become a trusted name in the manufacturing industry, delivering
            reliability, quality, and innovation.
          </p>
          <p>
            Our expertise covers a wide range of copper alloys, including{' '}
            <strong>Phosphorous Bronze, Brass, Gun Metal, High Tensile Brass, and Aluminium Bronze</strong>. By
            leveraging advanced processes such as <strong>Gravity Die Casting, Sand Casting, Continuous Casting, and
            Drawn Rods</strong>, we tailor manufacturing solutions to match alloy properties, production volumes, and
            customer-specific requirements.
          </p>
          <p>
            As a <strong>Tier-1 supplier to major OEMs</strong>, we provide critical components such as{' '}
            <strong>Bronze Bushings, Bushes, Washers, Rings, Impellers, and Gear Blanks</strong> for industries
            including tractors, earth movers, cranes, heavy machinery, and automotive applications. Through OEM global
            sourcing channels, our components are supplied to international facilities in{' '}
            <strong>Austria, China, Germany, Italy, and the USA</strong>.
          </p>
          <p>
            At Mato Industries, we offer <strong>end-to-end manufacturing facilities</strong>, from casting to
            precision machining, with stringent quality control at every stage.
          </p>
        </div>
      </Section>

      {/* Approach */}
      <Section title="Our Approach">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            {
              heading: 'Transparency',
              body: 'We work closely with every customer to understand their exact requirements. Digital work instructions and 3-year inspection data retention ensure full traceability.',
            },
            {
              heading: 'Precision',
              body: 'Every component is manufactured to the minutest detail of customer specifications and drawings, monitored via Cp/Cpk across all critical dimensions.',
            },
            {
              heading: 'Partnership',
              body: 'We strive to build long-term relationships through dependable delivery schedules, consistent quality, and a genuine commitment to meeting customer timelines.',
            },
          ].map(({ heading, body }) => (
            <div key={heading} className="bg-white border border-slate-200 rounded-2xl p-7 hover:shadow-md transition-shadow duration-200">
              <div className="w-2 h-2 bg-brand-500 rounded-full mb-4" aria-hidden="true" />
              <h3 className="text-lg font-bold text-navy-900 mb-2">{heading}</h3>
              <p className="text-slate-600 text-sm leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Quality */}
      <Section title="Quality">
        <div className="bg-white border border-slate-200 rounded-2xl p-8 md:p-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div className="space-y-4 text-slate-700 text-sm leading-relaxed">
              <p>
                As one of the oldest precision manufacturers in India, we successfully blend old-school craftsmanship
                with contemporary equipment to be a <strong>"Full Line" producer of Bronze Bearings, Bushings, and
                Thrust Washers</strong> for heavy-duty truck, trailer, and construction vehicle industries.
              </p>
              <p>
                Our manufacturing processes include turning, boring, broaching, grooving, facing, milling, drilling,
                grinding, and de-burring. Many processes are proprietary, with tooling made in our own tool room.
              </p>
              <p>
                Our quality lab features <strong>CMM, Contracer, and Spectrometer</strong> for precise measurement. We
                use <strong>SPC software</strong> for statistical process control, monitoring Cp and Cpk for all
                critical dimensions across 100% of in-process parts.
              </p>
            </div>
            <div className="space-y-3">
              {[
                { code: 'ISO 9001:2015', desc: 'Quality Management System' },
                { code: 'ISO 45001:2018', desc: 'Occupational Health & Safety' },
                { code: 'ISO 14001:2015', desc: 'Environmental Management' },
              ].map(({ code, desc }) => (
                <div key={code} className="flex items-center gap-4 p-4 bg-slate-50 border border-slate-100 rounded-xl">
                  <div className="w-10 h-10 bg-brand-100 rounded-lg flex items-center justify-center shrink-0">
                    <svg className="w-5 h-5 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <div className="font-bold text-navy-900 text-sm">{code}</div>
                    <div className="text-slate-500 text-xs">{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* Team */}
      <Section title="Our Directors">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl">
          {[
            { name: 'Mr. Ramesh Jain', role: 'Director, Founding Partner since 1968', image: '/images/rameshjain.jpg', alt: 'Mr. Ramesh Jain' },
            { name: 'Mr. Sumit Jain',  role: 'Director, Partner since 1995',          image: '/images/sumitjain.png',   alt: 'Mr. Sumit Jain' },
          ].map(({ name, role, image, alt }) => (
            <div key={name} className="flex flex-col items-center text-center bg-white border border-slate-200 rounded-2xl p-8 hover:shadow-md transition-shadow duration-200">
              <div className="relative mb-5">
                <img
                  src={image}
                  alt={alt}
                  width="112"
                  height="112"
                  className="w-28 h-28 object-cover rounded-full shadow-md ring-4 ring-brand-100"
                />
              </div>
              <h3 className="text-lg font-bold text-navy-900">{name}</h3>
              <p className="text-slate-500 text-sm mt-1">{role}</p>
            </div>
          ))}
        </div>
      </Section>

    </main>
    </div>
  </>
);

export default AboutUsPage;
